import { makeStyles, Paper, CircularProgress } from '@material-ui/core';
import cn from 'classnames';
import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { COLORS } from '../../theme/variables';
import { isMobile } from '../../utils/functions';
import { AlertState } from '../common';

const useStyles = makeStyles(() => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    padding: 0,
    borderRadius: 0,
  },
  listWrapper: {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100dvh - 116px)',
    maxHeight: 'calc(100dvh - 116px)',
    backgroundColor: COLORS.COLOR_GRAY_LIGHTENED_45,
    overflow: 'auto',
  },
  filtersBlock: {
    position: 'sticky',
    top: 0,
    left: 0,
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '14px 20px 14px 20px',
    backgroundColor: COLORS.COLOR_ADDITIONAL_WHITE,
    borderBottom: `1px solid ${COLORS.COLOR_GRAY_LIGHTENED_30}`,
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
    transition: 'transform 0.3s ease-in-out',
  },
  hiddenFilters: {
    transform: 'translateY(-100%)',
  },
  centerBlock: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flexGrow: 1,
    padding: 30,
    backgroundColor: COLORS.COLOR_GRAY_LIGHTENED_45,
    textAlign: 'center',
  },
  pageLoader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 30,
  },
}));

interface ListProps extends PropsWithChildren<{}> {
  prevId?: string;
  loadError?: string;
  isLoading?: boolean;
  isPageLoading?: boolean;
  hasMorePages?: boolean;
  filter?: JSX.Element | null;
  onNextPage?: () => void;
}

function List({
  loadError,
  isLoading,
  isPageLoading,
  hasMorePages,
  prevId,
  filter,
  onNextPage,
  children,
}: ListProps) {
  const classes = useStyles();

  const [showFilters, setShowFilters] = useState(true);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<HTMLDivElement | null>(null);
  const lastScrollTop = useRef(0);

  const handleObserver = useCallback(
    (entries) => {
      const target = entries[0];
      if (
        target.isIntersecting &&
        !isLoading &&
        !isPageLoading &&
        hasMorePages &&
        onNextPage
      ) {
        onNextPage();
      }
    },
    [hasMorePages, isLoading, isPageLoading, onNextPage],
  );

  useLayoutEffect(() => {
    if (!onNextPage) return;

    const observer = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: '0px 0px 800px 0px',
      threshold: 1.0,
    });

    const observerCurrentRef = observerRef.current;

    if (observerCurrentRef) {
      observer.observe(observerCurrentRef);
    }

    return () => {
      if (observerCurrentRef) {
        observer.unobserve(observerCurrentRef);
      }
    };
  }, [handleObserver, onNextPage]);

  useEffect(() => {
    if (!isLoading && prevId) {
      try {
        const targetElement = document.getElementById(prevId);
        if (targetElement) {
          targetElement.scrollIntoView({ behavior: 'smooth' });
        }
      } catch (error) {
        console.error(error);
      }
    }
  }, [isLoading, prevId]);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper || !filter) return;

    const threshold = 40;

    function handleScroll(e: Event) {
      const target = e.target as HTMLElement;
      const scrollTop = target.scrollTop;
      const diff = scrollTop - lastScrollTop.current;

      if (diff > threshold) {
        setShowFilters(false);
        lastScrollTop.current = scrollTop;
      } else if (diff < -threshold) {
        setShowFilters(true);
        lastScrollTop.current = scrollTop;
      }
    }

    wrapper.addEventListener('scroll', handleScroll);

    return () => {
      wrapper.removeEventListener('scroll', handleScroll);
    };
  }, [filter, wrapperRef]);

  useEffect(() => {
    if (isMobile()) {
      const body = document.querySelector('body');
      const baseLayout = document.getElementById('base-layout');

      if (body) {
        body.style.height = '100dvh';
      }

      if (baseLayout) {
        baseLayout.style.height = '100dvh';
      }
    }
  }, []);

  return (
    <Paper className={classes.container} data-testid='cards-list'>
      <div className={classes.listWrapper} ref={wrapperRef}>
        {!!filter && (
          <div
            className={cn(classes.filtersBlock, {
              [classes.hiddenFilters]: !showFilters,
            })}>
            {filter}
          </div>
        )}
        {isLoading ? (
          <div className={classes.centerBlock}>
            <CircularProgress size={36} color='primary' />
          </div>
        ) : loadError ? (
          <div className={classes.centerBlock}>
            <AlertState type='error'>{loadError}</AlertState>
          </div>
        ) : (
          <>
            {children}
            {isPageLoading && (
              <div className={classes.pageLoader}>
                <CircularProgress size={36} color='primary' />
              </div>
            )}
            {onNextPage && <div ref={observerRef} />}
          </>
        )}
      </div>
    </Paper>
  );
}

export default List;
